/** CSS serializer for existing sx/styled/makeStyles call sites. */
import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { alpha, deepMerge, defaultTheme, useTheme } from './theme';
const unitless = new Set('animationIterationCount aspectRatio borderImageOutset borderImageSlice borderImageWidth boxFlex boxFlexGroup boxOrdinalGroup columnCount columns fillOpacity flex flexGrow flexPositive flexShrink flexNegative flexOrder fontWeight gridArea gridRow gridRowEnd gridRowSpan gridRowStart gridColumn gridColumnEnd gridColumnSpan gridColumnStart lineClamp lineHeight opacity order orphans scale stopOpacity strokeDasharray strokeDashoffset strokeMiterlimit strokeOpacity strokeWidth tabSize widows zIndex zoom WebkitLineClamp'.split(' '));
const aliases = { m: ['margin'], mt: ['marginTop'], mr: ['marginRight'], mb: ['marginBottom'], ml: ['marginLeft'], mx: ['marginLeft', 'marginRight'], my: ['marginTop', 'marginBottom'], p: ['padding'], pt: ['paddingTop'], pr: ['paddingRight'], pb: ['paddingBottom'], pl: ['paddingLeft'], px: ['paddingLeft', 'paddingRight'], py: ['paddingTop', 'paddingBottom'], bgcolor: ['backgroundColor'] };
export const systemKeys = new Set([...Object.keys(aliases), ...'margin marginTop marginRight marginBottom marginLeft marginX marginY padding paddingTop paddingRight paddingBottom paddingLeft paddingX paddingY display displayPrint overflow textOverflow visibility whiteSpace flexDirection flexWrap justifyContent alignItems alignContent alignSelf justifyItems flex flexGrow flexShrink order gap rowGap columnGap gridTemplateColumns gridTemplateRows gridAutoFlow gridArea position top bottom left right zIndex width minWidth maxWidth height minHeight maxHeight boxSizing border borderTop borderBottom borderLeft borderRight borderColor borderRadius boxShadow color backgroundColor fontFamily fontSize fontStyle fontWeight lineHeight letterSpacing textAlign'.split(' ')]);
export const join = (...parts) => parts.filter(Boolean).join(' ');
export const getPath = (obj, path) => typeof path === 'string' ? path.split('.').reduce((a, k) => a == null ? undefined : a[k], obj) : undefined;
export function paletteValue(theme, value) { const found = getPath(theme.palette, value); return typeof found === 'string' ? found : value; }
export function resolveSx(input, theme = defaultTheme) {
    if (!input)
        return {};
    if (typeof input === 'function')
        return resolveSx(input(theme), theme);
    if (Array.isArray(input))
        return input.reduce((a, x) => deepMerge(a, resolveSx(x, theme)), {});
    const result = {};
    Object.entries(input).forEach(([key, original]) => {
        const value = typeof original === 'function' ? original(theme) : original;
        if (value == null || value === false)
            return;
        if (key.startsWith('@') || key.includes('&') || key.startsWith(':') || key.startsWith('.') || key.startsWith('>') || key.includes(' ')) {
            result[key] = resolveSx(value, theme);
            return;
        }
        if (key === 'typography' && typeof value === 'string') {
            Object.assign(result, theme.typography[value] || {});
            return;
        }
        if (key === 'displayPrint') {
            result['@media print'] = { ...(result['@media print'] || {}), display: value };
            return;
        }
        if (Array.isArray(value) || (typeof value === 'object' && Object.keys(value).every(k => k in theme.breakpoints.values))) {
            const pairs = Array.isArray(value) ? value.map((v, i) => [theme.breakpoints.keys[i], v]) : Object.entries(value).sort((a, b) => theme.breakpoints.values[a[0]] - theme.breakpoints.values[b[0]]);
            pairs.forEach(([bp, v]) => { if (v == null || !bp)
                return; const query = theme.breakpoints.up(bp); result[query] = deepMerge(result[query] || {}, resolveSx({ [key]: v }, theme)); });
            return;
        }
        if (typeof value === 'object') {
            result[key] = resolveSx(value, theme);
            return;
        }
        (aliases[key] || (key === 'marginX' ? aliases.mx : key === 'marginY' ? aliases.my : key === 'paddingX' ? aliases.px : key === 'paddingY' ? aliases.py : [key])).forEach(prop => {
            let v = value;
            if (/^(margin|padding)/.test(prop) || ['gap', 'rowGap', 'columnGap'].includes(prop))
                v = typeof v === 'number' ? theme.spacing(v) : v;
            else if (prop === 'borderRadius' && typeof v === 'number')
                v *= theme.shape.borderRadius;
            else if (/^border(Top|Right|Bottom|Left)?$/.test(prop) && typeof v === 'number')
                v = v === 0 ? 'none' : `${v}px solid`;
            else if (prop === 'boxShadow' && typeof v === 'number')
                v = theme.shadows[v] || 'none';
            else if (/^(color|backgroundColor|borderColor|borderTopColor|borderBottomColor|borderLeftColor|borderRightColor)$/.test(prop))
                v = paletteValue(theme, v);
            else if (['width', 'height', 'minWidth', 'minHeight', 'maxWidth', 'maxHeight'].includes(prop) && typeof v === 'number' && v > 0 && v <= 1)
                v = `${v * 100}%`;
            else if (prop === 'zIndex' && typeof v === 'string')
                v = theme.zIndex[v] ?? v;
            else if (prop === 'fontWeight' && typeof v === 'string')
                v = theme.typography[v] ?? v;
            else if (prop === 'fontFamily' && typeof v === 'string')
                v = getPath(theme.typography, v) ?? v;
            result[prop] = v;
        });
    });
    return result;
}
export const hash = s => { let h = 2166136261; for (let i = 0; i < s.length; i++)
    h = Math.imul(h ^ s.charCodeAt(i), 16777619); return (h >>> 0).toString(36); };
function propertyName(k) { return k.startsWith('--') ? k : k.replace(/[A-Z]/g, m => '-' + m.toLowerCase()).replace(/^ms-/, '-ms-'); }
export function serializeCss(selector, object) {
    let declarations = '', nested = '';
    Object.entries(object || {}).forEach(([key, value]) => {
        if (value == null || value === false)
            return;
        if (typeof value === 'object' && !Array.isArray(value)) {
            if (key.startsWith('@media') || key.startsWith('@supports') || key.startsWith('@container'))
                nested += `${key}{${serializeCss(selector, value)}}`;
            else if (key.startsWith('@keyframes'))
                nested += `${key}{${Object.entries(value).map(([step, styles]) => serializeCss(step, styles)).join('')}}`;
            else if (key === '@global')
                Object.entries(value).forEach(([s, styles]) => { nested += serializeCss(s, styles); });
            else {
                const parts = key.split(',').map(k => k.trim());
                const expanded = selector.split(',').flatMap(s => parts.map(k => k.includes('&') ? k.replace(/&/g, s) : `${s} ${k}`)).join(',');
                nested += serializeCss(expanded, value);
            }
        }
        else {
            (Array.isArray(value) ? value : [value]).forEach(v => {
                const suffix = typeof v === 'number' && v !== 0 && !unitless.has(key) && !key.startsWith('--') ? 'px' : '';
                declarations += `${propertyName(key)}:${v}${suffix};`;
            });
        }
    });
    return (declarations ? `${selector}{${declarations}}` : '') + nested;
}
const sheets = new Map();
function acquireCss(id, css) {
    if (typeof document === 'undefined' || !css)
        return () => { };
    let entry = sheets.get(id);
    if (!entry) {
        const element = document.createElement('style');
        element.dataset.a2zStyle = id;
        const nonce = document.querySelector('meta[name="csp-nonce"]')?.content;
        if (nonce)
            element.setAttribute('nonce', nonce);
        element.textContent = css;
        document.head.appendChild(element);
        entry = { element, references: 0 };
        sheets.set(id, entry);
    }
    entry.references++;
    return () => { entry.references--; if (entry.references <= 0) {
        entry.element.remove();
        sheets.delete(id);
    } };
}
export function useCss(styles, isSx = true) {
    const theme = useTheme();
    const resolved = isSx ? resolveSx(styles, theme) : styles;
    const signature = JSON.stringify(resolved || {});
    const id = useMemo(() => `a2z-${hash(signature)}`, [signature]);
    const css = useMemo(() => serializeCss(`.${id}`, JSON.parse(signature)), [id, signature]);
    useLayoutEffect(() => acquireCss(id, css), [id, css]);
    return signature === '{}' ? '' : id;
}
export function useGlobalCss(styles) {
    const signature = JSON.stringify(styles);
    const id = `global-${hash(signature)}`;
    useLayoutEffect(() => acquireCss(id, Object.entries(styles).map(([s, o]) => s.startsWith('@media') ? `${s}{${Object.entries(o).map(([selector, rule]) => serializeCss(selector, rule)).join('')}}` : serializeCss(s, o)).join('')), [id, signature]);
}
let nextId = 0;
export function useStableId(explicit) { const ref = useRef(null); if (!ref.current)
    ref.current = `a2z-control-${++nextId}`; return explicit || ref.current; }
export const setRef = (ref, value) => { if (typeof ref === 'function')
    ref(value);
else if (ref)
    ref.current = value; };
export const mergeRefs = (...refs) => value => refs.forEach(ref => setRef(ref, value));
export function useControlled(value, initial) { const [state, setState] = React.useState(initial); return [value !== undefined ? value : state, next => { if (value === undefined)
        setState(next); }]; }
const excluded = new Set('sx classes component as theme ownerState variant size color fullWidth disableRipple disableTouchRipple disableFocusRipple disableElevation centerRipple focusRipple focusVisibleClassName action TouchRippleProps elevation square container item xs sm md lg xl spacing rowSpacing columnSpacing columns zeroMinWidth alignContent justifyContent alignItems disableGutters dense inset divider selected disableTypography primaryTypographyProps secondaryTypographyProps InputProps inputProps InputLabelProps FormHelperTextProps SelectProps slotProps slots TransitionProps TransitionComponent PaperProps MenuListProps BackdropProps ModalProps keepMounted disablePortal hideBackdrop disableAutoFocus disableEnforceFocus disableRestoreFocus disableScrollLock disableEscapeKeyDown maxWidth fullScreen margin error helperText label children'.split(' '));
export function domProps(props, omit = []) {
    const out = {};
    const skip = new Set(omit);
    Object.entries(props).forEach(([key, value]) => {
        if (excluded.has(key) || systemKeys.has(key) || skip.has(key) || /^m(color|Color|bgcolor|width|height|padding|borderradius)$/.test(key) || key === 'haveOffer')
            return;
        if (value !== undefined)
            out[key] = value;
    });
    return out;
}
export function useRootStyles(name, props, defaults = {}) {
    const theme = useTheme();
    const system = {};
    Object.keys(props).forEach(k => { if (systemKeys.has(k))
        system[k] = props[k]; });
    const override = theme.components?.[`Ui${name}`]?.styleOverrides?.root;
    const typeName = { Button: 'button', Tab: 'button', InputBase: 'body1', InputLabel: 'body1', FormLabel: 'body1', FormHelperText: 'caption', TableCell: 'body2', MenuItem: 'body1', DataGrid: 'body2', Snackbar: 'body2', Alert: 'body2' }[name];
    const selection = ['ListItemButton', 'TableRow'].includes(name) ? { '--ui-option-selected': alpha(theme.palette.primary.main, 0.08) } : {};
    return useCss([selection, typeName ? { letterSpacing: theme.typography[typeName]?.letterSpacing } : {}, defaults, typeof override === 'function' ? override({ theme, ownerState: props }) : override, system, props.sx]);
}
export function styled(Component, options = {}) {
    return (...rules) => {
        const Wrapped = React.forwardRef(function LocalStyled(props, ref) {
            const theme = useTheme();
            let css = {};
            rules.forEach(rule => { css = deepMerge(css, typeof rule === 'function' ? rule({ ...props, theme }) : rule || {}); });
            const className = useCss(deepMerge(css, resolveSx(props.sx, theme)), false);
            const forwarded = {};
            Object.entries(props).forEach(([k, v]) => { if (k === 'sx' || !options.shouldForwardProp || options.shouldForwardProp(k))
                forwarded[k] = v; });
            const C = props.as || Component;
            return React.createElement(C, { ...(typeof C === 'string' ? domProps(forwarded) : forwarded), ref, className: join(className, props.className) });
        });
        Wrapped.displayName = `Styled(${typeof Component === 'string' ? Component : Component.displayName || Component.name || 'Component'})`;
        return Wrapped;
    };
}
export function makeStyles(styles) {
    return function useLocalStyles(props = {}) {
        const theme = useTheme();
        const source = typeof styles === 'function' ? styles(theme) : styles;
        const resolved = {};
        Object.entries(source || {}).forEach(([k, v]) => { resolved[k] = typeof v === 'function' ? v(props) : v; });
        const signature = JSON.stringify(resolved);
        const classes = {};
        Object.keys(resolved).forEach(k => { classes[k] = `a2z-${hash(signature)}-${k}`; });
        const css = Object.entries(resolved).map(([k, v]) => serializeCss(`.${classes[k]}`, v)).join('').replace(/\$([\w-]+)/g, (_, k) => '.' + (classes[k] || k));
        useLayoutEffect(() => acquireCss('rules-' + hash(signature), css), [signature, css]);
        return classes;
    };
}
export const withStyles = styles => Component => {
    const useStyles = makeStyles(styles);
    return React.forwardRef(function WithLocalStyles(props, ref) { const classes = useStyles(props); return React.createElement(Component, { ...props, ref, classes: { ...classes, ...props.classes }, className: join(classes.root, props.className) }); });
};
export function keyframes(strings, ...values) {
    const text = Array.isArray(strings) ? strings.reduce((a, s, i) => a + s + (values[i] ?? ''), '') : String(strings);
    const id = `a2z-animation-${hash(text)}`;
    // Definitions live for the module lifetime, as exported animation names may be reused later.
    if (typeof document !== 'undefined' && !sheets.has(id))
        acquireCss(id, `@keyframes ${id}{${text}}`);
    return id;
}
